import { default as _Vue } from 'vue';
import { Store, Module } from 'vuex';

// Entities
import EntityStations from './EntityStations';
import EntitySoles from './EntitySoles';
import EntityPatients from './EntityPatients';
import EntityAcquisitions from './EntityAcquisitions';
import EntityCommands from './EntityCommands';

import { AbstractService } from '@/plugins/podolib/lib/AbstractService';
import { TQuery } from '@/plugins/podolib/services/entities/Entity';

// Service options type
export type TOptions = {
  stations: {
    queryParams: Partial<TQuery>;
  };
  soles: {
    queryParams: Partial<TQuery>;
  };
  patients: {
    queryParams: Partial<TQuery>;
  };
  acquisitions: {
    queryParams: Partial<TQuery>;
  };
  commands: {
    queryParams: Partial<TQuery>;
  };
};

// Entity interface
export interface IEntity<TState, RootState> {
  readonly defaultState: TState;
  readonly storeModule: Module<TState, RootState>;

  sync(): Promise<void>;
}

/**
 * @author Damien CORLER <d.corler@zhortech.com>
 * @since 1.0.0
 * @version 1.0.0
 */
export default class EntitiesService extends AbstractService<TOptions, any> {
  private _synchronizedAtLeastOnce: boolean = false;
  private entities: any;
  public stations!: EntityStations;
  public soles!: EntitySoles;
  public patients!: EntityPatients;
  public acquisitions!: EntityAcquisitions;
  public commands!: EntityCommands;

  /**
   * @param options
   */
  constructor(options: TOptions) {
    super(options, 'entities');
  }

  /**
   * Initialize plugin
   */
  async init(Vue: typeof _Vue, store: Store<any>): Promise<void> {
    await super.init(Vue, store);

    this.entities = {
      stations: new EntityStations(this.Vue, this.store, this.logger, this.options.stations),
      soles: new EntitySoles(this.Vue, this.store, this.logger, this.options.soles),
      patients: new EntityPatients(this.Vue, this.store, this.logger, this.options.patients),
      acquisitions: new EntityAcquisitions(this.Vue, this.store, this.logger, this.options.acquisitions),
      commands: new EntityCommands(this.Vue, this.store, this.logger, this.options.commands),
    };

    const entityEntries = Object.entries(this.entities);
    for (let entity of entityEntries) {
      (<any>this)[entity[0]] = entity[1];
    }

    this.$register(null, {
      modules: entityEntries.reduce((acc, obj) => {
        // @ts-ignore TODO remove this ignore
        acc[obj[0]] = obj[1].storeModule;
        return acc;
      }, {}),
    });
  }

  async sync(): Promise<void> {
    try {
      this.logger.log('Start synchronization ...');

      /*await this.entities.stations.sync({ mergeWithLastUsedQuery: true });
      await this.entities.soles.sync({ mergeWithLastUsedQuery: true });
      await this.entities.patients.sync({ mergeWithLastUsedQuery: true });
      await this.entities.acquisitions.sync({ mergeWithLastUsedQuery: true });
      await this.entities.commands.sync({ mergeWithLastUsedQuery: true });*/

      await Promise.all(
        Object.values(this.entities).map((x: any) => {
          return x.sync({ mergeWithLastUsedQuery: true });
        }),
      );

      this._synchronizedAtLeastOnce = true;
      this.Vue.prototype.$pdc.bus.emit('entities:synced');

      this.logger.success('Synchronization completed');
    } catch (e) {
      this.logger.fatal('Synchronization failed');
      // Throw
      throw e;
    }
  }

  get synchronizedAtLeastOnce(): boolean {
    return this._synchronizedAtLeastOnce;
  }
}
